import React, { useState, useEffect } from "react";
import { NumericFormat } from "react-number-format";
import Button from "../ui/Button";
import Share from "../flight-brief/Share";
import Modal from "../ui/Modal";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router";
import movidaLogo from "../../assets/icons/movida-logo.png";
import infoIcon from "../../assets/icons/info-blue.svg";
import CloseCross from "../ui/CloseCross";
import getDenomination, { getCurrencyCode } from "../../utilities/getDenomination";
import { MOVIDA_POINTS_PROGRAM } from "../../utilities/quoteMessaging";

/** Renders a currency amount as £10,000 GBP. */
const PriceTag = ({ code, amount, variant = "preferred" }) => (
    <div className={`price-and-share__price price-and-share__price--${variant}`}>
        {getDenomination(code)}
        <NumericFormat displayType="text" value={amount} thousandSeparator decimalScale={0} />
        &nbsp;{getCurrencyCode(code)}
    </div>
);

/** Renders the flip-side info overlay with a consistent header + close. */
const InfoOverlay = ({ title, onClose, children }) => (
    <>
        <div className="price-and-share__info-overlay__header">
            <h3 className="price-and-share__info-overlay__title">{title}</h3>
            <CloseCross onClick={onClose} />
        </div>
        <div className="price-and-share__info-overlay__content">{children}</div>
    </>
);

const PriceAndShare = ({
    price,
    operatorCurrency,
    currency,
    expanded = false,
    showMovida = false,
    sharedPage = false,
    legId,
    aircraftId,
    tailNumber,
    onBookingClick,
    onInfoClick,
    minimal = false,
}) => {
    const [showSharePanel, setShowSharePanel] = useState(false);
    const [infoType, setInfoType] = useState(null); // 'price', 'movida', or null
    const [lastInfoType, setLastInfoType] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    useEffect(() => {
        if (!infoType) return;
        const timeout = setTimeout(() => setInfoType(null), 10000);
        return () => clearTimeout(timeout);
    }, [infoType]);

    // Keep content visible during flip-back animation
    if (infoType && infoType !== lastInfoType) {
        setLastInfoType(infoType);
    }

    const goToBookingPage = () => {
        if (onBookingClick) return onBookingClick();
        navigate(`/book?lo=${legId}&name=${queryParams.get("name")}`, {
            state: { legid: legId, arfq: aircraftId, tailNum: tailNumber },
        });
    };

    const preferredKey = currency?.toLowerCase();
    const operatorKey = operatorCurrency?.toLowerCase();
    const preferredAmount = price && preferredKey ? price[preferredKey] : undefined;
    const operatorAmount = price && operatorKey ? price[operatorKey] : undefined;
    const movidaPoints = price?.gbp ? Math.floor(price.gbp / 100) : 0;

    const isFlipped = !!infoType;
    const activeInfoType = infoType || lastInfoType;
    const showPreferredOnly = minimal && expanded && currency;
    const showOperatorSecondary = operatorCurrency && operatorAmount && (operatorCurrency !== currency || !minimal);

    return (
        <div
            className={`price-and-share-wrapper${isFlipped ? " price-and-share-wrapper--flipped" : ""}${minimal ? " price-and-share-wrapper--minimal" : ""}`}
        >
            <div className="price-and-share-inner">
                <div className="price-and-share-front">
                    <div
                        className={`price-and-share${expanded ? " price-and-share--expanded" : ""}${minimal ? " price-and-share--minimal" : ""}`}
                    >
                        <div className="price-and-share__price-row">
                            {showPreferredOnly ? (
                                <div className="price-and-share__preferred">
                                    <PriceTag code={currency} amount={preferredAmount ?? operatorAmount} />
                                </div>
                            ) : (
                                price && (
                                    <>
                                        {preferredAmount ? (
                                            <>
                                                <PriceTag code={currency} amount={preferredAmount} />
                                                {showOperatorSecondary && (
                                                    <>
                                                        <span className="price-and-share__price-separator">|</span>
                                                        <PriceTag
                                                            code={operatorCurrency}
                                                            amount={operatorAmount}
                                                            variant="customer"
                                                        />
                                                    </>
                                                )}
                                            </>
                                        ) : (
                                            operatorAmount && (
                                                <PriceTag code={operatorCurrency} amount={operatorAmount} />
                                            )
                                        )}
                                        {!minimal && (currency || operatorCurrency) && (
                                            <button
                                                className="price-and-share__info"
                                                onClick={() => setInfoType("price")}
                                            >
                                                <img src={infoIcon} alt="Click for price info" />
                                            </button>
                                        )}
                                    </>
                                )
                            )}
                        </div>

                        {!sharedPage && (
                            <>
                                <Button classNames={["button--full"]} onClick={goToBookingPage}>
                                    {expanded ? "RESERVE NOW" : "Reserve Now"}
                                </Button>
                                {!minimal && (
                                    <div className="price-and-share__buttons-row">
                                        <Button
                                            classNames={["button--white"]}
                                            onClick={() => setShowSharePanel(true)}
                                            icon="share-black"
                                        >
                                            Share
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}

                        {!minimal && showMovida && price?.gbp && (
                            <div className="price-and-share__movida-points">
                                <div className="price-and-share__movida-points-header">
                                    <div className="price-and-share__movida-points-label-wrapper">
                                        <img src={movidaLogo} alt="Movida" className="price-and-share__movida-logo" />
                                        <div className="price-and-share__movida-points-label">MOVIDA POINTS</div>
                                    </div>
                                    <div className="price-and-share__movida-points-value-wrapper">
                                        <div className="price-and-share__movida-points-value">{movidaPoints}</div>
                                        <button
                                            className="price-and-share__movida-points-info-button"
                                            onClick={() => setInfoType("movida")}
                                        >
                                            <img
                                                src={infoIcon}
                                                alt="Movida points info"
                                                className="price-and-share__movida-points-info-icon"
                                            />
                                        </button>
                                    </div>
                                </div>
                                {expanded && (
                                    <div className="price-and-share__movida-points-text">
                                        This booking could earn you {movidaPoints} MOVIDA points
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className="price-and-share-back">
                    {!minimal && (
                        <div className="price-and-share__info-overlay">
                            {activeInfoType === "price" && (
                                <InfoOverlay title="Price Information" onClose={() => setInfoType(null)}>
                                    <p>
                                        Price shown in {operatorCurrency} is the original price provided by the Operator
                                    </p>
                                </InfoOverlay>
                            )}
                            {activeInfoType === "movida" && (
                                <InfoOverlay title="MOVIDA Points" onClose={() => setInfoType(null)}>
                                    <p>{MOVIDA_POINTS_PROGRAM}</p>
                                </InfoOverlay>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {!minimal && (
                <Modal
                    classNames={["modal__content--thin"]}
                    modalVisible={showSharePanel}
                    closeModal={() => setShowSharePanel(false)}
                >
                    <Share
                        type="flight"
                        endpoint={"/api/frontend/share-quote"}
                        shareRef={[aircraftId]}
                        close={() => setShowSharePanel(false)}
                        tailNumbers={[tailNumber]}
                    />
                </Modal>
            )}
        </div>
    );
};

export default PriceAndShare;
